import * as React from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from './utils';

/**
 * FocusedPageShell — single-decision-per-page layout.
 *
 * Visual grammar (informed by an external single-decision design system
 * reference; concrete parameters):
 *   - centered single column, max-width 628px (`max-w-focused`)
 *   - top: optional back arrow + title (h1) + optional description
 *   - middle: caller-supplied content (form fields)
 *   - bottom: sticky action bar with a vertical fade-in from transparent
 *     to background (so content above the toolbar visually dissolves
 *     under the sticky CTA instead of meeting a hard backdrop edge).
 *     Inner CTA is capped at 343px (`max-w-focused-toolbar`) so the
 *     button feels deliberate on desktop and full-bleed on mobile.
 *
 * The shell owns the layout chrome only. Title text, field rendering and
 * Continue button label / disabled state are caller decisions.
 *
 * `onBack` is the contract for "go to previous step" — the shell renders
 * the back arrow only when `onBack` is provided. First step in a wizard
 * passes `undefined` and the arrow disappears.
 *
 * The footer is rendered as a separate slot so the caller controls the
 * exact button shape (variant, size, label, type=submit vs button). This
 * keeps the shell agnostic to the underlying form library.
 */

export interface FocusedPageShellProps {
  /** Page title — rendered as h1, top of the page. */
  title: string;
  /** Optional muted-tone subtitle below the title. */
  description?: React.ReactNode;
  /** Optional callback for the back arrow. Hidden when undefined. */
  onBack?: () => void;
  /** Body content — typically the form fields. */
  children: React.ReactNode;
  /**
   * Sticky bottom toolbar slot — typically a single primary Button.
   * Pass `null` when the page renders its own action button inside the
   * body (e.g. the documents page where the action lives next to the
   * upload-completion summary). When null, the sticky toolbar is omitted
   * and the body uses the regular page-bottom padding.
   */
  footer?: React.ReactNode;
  /** Optional extra className on the outermost wrapper. */
  className?: string;
}

export function FocusedPageShell({
  title,
  description,
  onBack,
  children,
  footer,
  className,
}: FocusedPageShellProps): JSX.Element {
  const hasFooter = footer !== undefined && footer !== null;
  return (
    <div
      className={cn(
        'mx-auto flex min-h-full w-full max-w-focused flex-col px-4 pt-6 sm:px-6 sm:pt-10',
        hasFooter ? 'pb-32' : 'pb-12',
        className,
      )}
    >
      <header className="mb-8 flex flex-col gap-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground/5 text-foreground transition-colors hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Indietro"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
          {title}
        </h1>
        {description ? (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        ) : null}
      </header>

      <main className="flex flex-1 flex-col gap-5">{children}</main>

      {hasFooter ? (
        <div className="fixed inset-x-0 bottom-0 z-sticky">
          {/* Vertical fade-in: top half is fully transparent, the bottom
           * half resolves to the page background. Lets the content above
           * the toolbar dissolve under the sticky CTA without a hard
           * edge. Same idea as a mask-image gradient on a solid bg, but
           * expressed as a real gradient for portability. */}
          <div
            className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-background/85 to-background"
            aria-hidden
          />
          <div className="mx-auto w-full max-w-focused-toolbar px-4 py-5 sm:px-6">
            {footer}
          </div>
        </div>
      ) : null}
    </div>
  );
}
